/**
 * @file api/workspace.js
 * @location src/integrations/supabase/api/workspace.js
 * @role 로그인 후 팀 데이터 일괄 로드(하이드레이션) — reducer seed 페이로드 생성.
 *
 * RLS 가 모든 select 를 본인 팀으로 스코프하므로 단순 select 로 충분.
 * tasks 는 deleted_at 유무로 tasks / deletedTasks 분리 (기존 reducer 형태 호환).
 */
import { supabase } from '../client.js';
import { rowToTask, rowToMember, rowToTemplate } from '../mappers.js';
import { getSubscriptionState } from './subscription.js';
import { getAccountState } from './account.js';

/** authUserId 에 연결된 member.id 조회 (없으면 null = 미가입). */
export async function fetchMyMemberId(authUserId) {
  const { data, error } = await supabase
    .from('members').select('id').eq('auth_user_id', authUserId).maybeSingle();
  if (error) throw error;
  return data?.id ?? null;
}

/**
 * 팀 전체 데이터 로드. 미가입(member 없음) 이면 null 반환.
 *
 *  subscription 은 별도 RPC(table-return) 라 callRpc 경유 — 실패해도 hydrate 자체는
 *  막지 않음(트라이얼 기본값으로 fallback. 0007 migration 의 teams.subscription_status
 *  default='trial' 이 보장).
 *
 * @returns {null | { uid, companyName, members, tasks, deletedTasks, taskTemplates, subscription }}
 */
export async function fetchWorkspace(authUserId) {
  const myId = await fetchMyMemberId(authUserId);
  if (!myId) return null;

  const [teamRes, membersRes, tasksRes, tmplRes, subscription, deletedScope] = await Promise.all([
    supabase.from('teams').select('name').limit(1).maybeSingle(),
    supabase.from('members').select('*'),
    supabase.from('tasks').select('*'),
    supabase.from('task_templates').select('*'),
    getSubscriptionState().catch(() => null),
    getAccountState().catch(() => null),
  ]);
  for (const r of [teamRes, membersRes, tasksRes, tmplRes]) {
    if (r.error) throw r.error;
  }

  const allTasks = (tasksRes.data ?? []).map(rowToTask);
  return {
    uid: myId,
    companyName: teamRes.data?.name ?? '',
    members: (membersRes.data ?? []).map(rowToMember),
    tasks: allTasks.filter(t => !t.deletedAt),
    deletedTasks: allTasks.filter(t => t.deletedAt),
    taskTemplates: (tmplRes.data ?? []).map(rowToTemplate),
    subscription,
    deletedScope,
  };
}
